// frontend/src/components/loi/LoiWhatIfPrice.tsx
/**
 * The what-if price (spec 3.9, corrected): the price at which the ANALYSIS SCREEN'S going-in cap reads the target,
 * priced by the backend from the analysis's own saved figures. ⛔ The deal's price changes ONLY on
 * "Make this the deal's number". Every note is to him, never into the letter. Copy uses plain hyphens only.
 */
import { useState } from 'react';
import { Button } from '../ui';
import { api } from '../../lib/api';
import type { DealScenario } from '../../types/loiOverride';

const usd = (n: number): string => `$${Math.round(n).toLocaleString('en-US')}`;
/**
 * ⛔ Ruling 27a: `deal_analyses.updated_at` is nullable, and `new Date(null)` is the epoch - "Dec 31, 1969" in LA. No date
 *   (or none that parses) is left out, never made up.
 */
const day = (iso: string | null): string | null => {
  const d = iso ? new Date(iso) : null;
  return d && !Number.isNaN(d.getTime())
    ? d.toLocaleDateString('en-US', { timeZone: 'America/Los_Angeles', month: 'short', day: 'numeric', year: 'numeric' })
    : null;
};
/**
 * A target as it was PRICED - at least 2 decimals, up to 3 - the way the server's label prints it (Ruling 20f), so the
 * note under "Price at 6.125% cap" never calls the same price "a 6.13% cap".
 */
const pct = (n: number): string => {
  const three = n.toFixed(3);
  return three.endsWith('0') ? three.slice(0, -1) : three;
};

export default function LoiWhatIfPrice({ dealId, dealOfferPrice, onUsePrice, onPromoted, priceBoxEdits = 0 }: {
  dealId: string;
  dealOfferPrice: number | null;
  onUsePrice: (price: number) => void;
  /**
   * ⛔ I1: the deal's price changed - whatever holds a copy of the deal must take it, or its next routine Save writes the
   *   old price back over his promotion.
   * ⛔⛔ B1: and it names WHICH deal - the one this what-if was priced on. An answer can arrive after he has moved to
   *    another deal (the page is reused across deals); the host takes it only onto that deal.
   */
  onPromoted?: (offerPrice: number, dealId: string) => void;
  /**
   * ⛔ Ruling 27b: how many times he has edited the letter's price box himself, counted by the dialog ("Use this price"
   *   is not an edit of his). A note about THIS LETTER's price names the box's contents, so it goes once this moves.
   */
  priceBoxEdits?: number;
}) {
  const [cap, setCap] = useState('6.00');
  const [busy, setBusy] = useState(false);
  const [scenario, setScenario] = useState<DealScenario | null>(null);
  /**
   * The note under the what-if. `boxEdit` is the price-box count when a note about THIS LETTER was written - it shows only
   *   while that count stands (Ruling 27b); null for a note about the DEAL, which his edit of the box does not change.
   */
  const [note, setNote] = useState<{ text: string; boxEdit: number | null } | null>(null);
  const [error, setError] = useState<string | null>(null);
  /** ⛔ Once he makes a what-if the deal's number, THAT is the deal's price - a later note must not name the old one. */
  const [promotedTo, setPromotedTo] = useState<number | null>(null);
  const dealPrice = promotedTo ?? dealOfferPrice;
  const run = async (fn: () => Promise<void>) => {
    setBusy(true);
    setError(null);
    try { await fn(); } catch (e) { setError(e instanceof Error ? e.message : 'That did not work.'); } finally { setBusy(false); }
  };
  /** ⛔ The last answer goes the moment he asks again: a refused ask must never leave an older price on screen to use. */
  const priceIt = () => void run(async () => {
    setNote(null);
    setScenario(null);
    setScenario((await api.createPriceAtCapScenario(dealId, Number(cap))).scenario);
  });
  const savedOn = scenario ? day(scenario.basis.analysisSavedAt) : null;
  return (
    <div className="mt-3 rounded border border-gray-200 p-2 text-[12.5px]" data-testid="loi-what-if">
      <label className="text-gray-700">
        Target cap (%)
        {/* ⛔⛔ No `name` - it is not the letter. And it sits INSIDE the letter's form: Enter in a one-line box submits
              the form - Generate, which FILES the letter on a deal. Enter here prices it, nothing more. */}
        <input className="ml-2 w-20 rounded border-gray-300 text-sm" inputMode="decimal" value={cap} onChange={(e) => setCap(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); if (!busy) priceIt(); } }} />
      </label>
      <Button type="button" variant="outline" className="ml-2" disabled={busy} onClick={priceIt}>
        Price it
      </Button>
      {error && <p className="mt-1 text-amber-900">{error}</p>}
      {scenario && (
        <div className="mt-2">
          <p>{`${scenario.label}: ${usd(scenario.purchasePrice)} - going-in NOI ${usd(scenario.basis.goingInNoi)}, from the ${savedOn ? `analysis saved ${savedOn}` : 'saved analysis'}.`}</p>
          <div className="mt-1 flex flex-wrap gap-2">
            <Button type="button" variant="outline" onClick={() => {
              onUsePrice(scenario.purchasePrice);
              // Ruling 24: "the deal's price" - the dialog's price is `offer_price` OR `deal_value`.
              setNote({
                text: dealPrice !== null && dealPrice !== scenario.purchasePrice
                  ? `This letter's price is at a ${pct(scenario.basis.targetCapPct)}% cap; the deal's price stays ${usd(dealPrice)}.`
                  : `This letter's price is at a ${pct(scenario.basis.targetCapPct)}% cap.`,
                boxEdit: priceBoxEdits,
              });
            }}>Use this price in the letter</Button>
            <Button type="button" variant="outline" disabled={busy}
              onClick={() => void run(async () => {
                const r = await api.promoteDealScenario(dealId, scenario.id);
                setPromotedTo(r.offerPrice);
                // `dealId` is the one this press was made on - the prop as it stood at the click, not as it stands now.
                onPromoted?.(r.offerPrice, dealId);
                // The server's `previousOfferPrice` IS `crm_deals.offer_price`, so this note keeps "offer price".
                setNote({
                  text: `The deal's offer price is now ${usd(r.offerPrice)}${r.previousOfferPrice !== null ? ` (it was ${usd(r.previousOfferPrice)})` : ''}.`,
                  boxEdit: null,
                });
              })}>
              Make this the deal's number
            </Button>
          </div>
        </div>
      )}
      {note && (note.boxEdit === null || note.boxEdit === priceBoxEdits) && <p className="mt-1 text-gray-600">{note.text}</p>}
    </div>
  );
}
